import {
	AlertTriangle,
	RotateCcw,
	Save,
	Search,
	ShieldCheck,
	SlidersHorizontal,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { type Matter, useMattersCache } from "@/shared/cache";
import { ComboBox } from "@/shared/components/ComboBox/ComboBox";
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from "@/shared/components/ui/alert";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/shared/components/ui/select";
import { cn } from "@/shared/lib/utils";
import { useApi } from "@/shared/utils/request";
import {
	getAccessPolicy,
	listAccessPolicyProfessionals,
	replaceAccessPolicy,
	resetAccessPolicy,
} from "../access-policies/api";
import {
	buildReplaceAccessPolicyBody,
	type ClientOption,
	clientOptionsFromMatters,
	DEFAULT_PROFESSIONAL_FILTERS,
	deselectProfessionals,
	draftFromPolicy,
	hasDraftChanges,
	matterScopeParts,
	type ProfessionalFilters,
	professionalMatchesFilters,
	professionalMatchesSearch,
	selectProfessionals,
	toggleProfessional,
	uniqueProfessionalValues,
	updateDraftMode,
} from "../access-policies/model";
import type {
	AccessPolicy,
	AccessPolicyDraft,
	AccessPolicyMode,
	AccessPolicyProfessional,
} from "../access-policies/types";
import styles from "./AdminViews.module.css";

const EMPTY_DRAFT: AccessPolicyDraft = {
	mode: "deny_list",
	professionalIds: [],
};

const clientSearchFields = [
	(client: ClientOption) => ({ value: client.code, weight: 1.5 }),
	(client: ClientOption) => ({ value: client.name, weight: 1 }),
];

const matterSearchFields = [
	(matter: Matter) => ({
		value: matterScopeParts(matter).matterId,
		weight: 1.5,
	}),
	(matter: Matter) => ({ value: matter.matter ?? "", weight: 1.2 }),
	(matter: Matter) => ({ value: matter.description, weight: 1 }),
];

function errorMessage(error: unknown, fallback: string): string {
	return error instanceof Error ? error.message : fallback;
}

function clientOptionLabel(client: ClientOption): string {
	return client.name && client.name !== client.code
		? `${client.code} — ${client.name}`
		: client.code;
}

function matterOptionLabel(matter: Matter): string {
	const { matterId } = matterScopeParts(matter);
	const description = matter.description.trim();
	return description ? `${matterId} — ${description}` : matterId;
}

function modeDescription(mode: AccessPolicyMode): {
	title: string;
	description: string;
	listLabel: string;
} {
	if (mode === "allow_list") {
		return {
			title: "Allow list",
			description:
				"Only selected professionals can access this scope. Everyone else is denied.",
			listLabel: "Professionals who can access",
		};
	}

	return {
		title: "Deny list",
		description:
			"Selected professionals cannot access this scope. Everyone else can.",
		listLabel: "Professionals who cannot access",
	};
}

export default function AccessPoliciesView() {
	const { makeApiRequest } = useApi();
	const { matters, isLoading: mattersLoading } = useMattersCache();
	const [clientCode, setClientCode] = useState("");
	const [matterId, setMatterId] = useState("");
	const [policy, setPolicy] = useState<AccessPolicy | null>(null);
	const [draft, setDraft] = useState<AccessPolicyDraft>(EMPTY_DRAFT);
	const [professionals, setProfessionals] = useState<
		AccessPolicyProfessional[]
	>([]);
	const [professionalSearch, setProfessionalSearch] = useState("");
	const [professionalFilters, setProfessionalFilters] =
		useState<ProfessionalFilters>(DEFAULT_PROFESSIONAL_FILTERS);
	const [showSelectedOnly, setShowSelectedOnly] = useState(false);
	const [policyLoading, setPolicyLoading] = useState(false);
	const [professionalsLoading, setProfessionalsLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [policyError, setPolicyError] = useState<string | null>(null);
	const [professionalsError, setProfessionalsError] = useState<string | null>(
		null,
	);

	const clientOptions = useMemo(
		() => clientOptionsFromMatters(matters),
		[matters],
	);
	const matterOptions = useMemo(
		() =>
			matters
				.filter((matter) => {
					const scope = matterScopeParts(matter);
					return scope.clientCode === clientCode && !!scope.matterId;
				})
				.sort((left, right) =>
					matterScopeParts(left).matterId.localeCompare(
						matterScopeParts(right).matterId,
					),
				),
		[clientCode, matters],
	);
	const dirty = useMemo(
		() => policy !== null && hasDraftChanges(policy, draft),
		[draft, policy],
	);
	const selectedIds = useMemo(
		() => new Set(draft.professionalIds),
		[draft.professionalIds],
	);
	const availableProfessionals = useMemo(() => {
		const byId = new Map(
			professionals.map((professional) => [professional.profId, professional]),
		);
		for (const professional of policy?.professionals ?? []) {
			byId.set(professional.profId, professional);
		}
		return [...byId.values()].sort((left, right) =>
			(left.name || left.email).localeCompare(right.name || right.email),
		);
	}, [policy, professionals]);
	const roleOptions = useMemo(
		() => uniqueProfessionalValues(availableProfessionals, "role"),
		[availableProfessionals],
	);
	const profTypeOptions = useMemo(
		() => uniqueProfessionalValues(availableProfessionals, "profType"),
		[availableProfessionals],
	);
	const departmentOptions = useMemo(
		() => uniqueProfessionalValues(availableProfessionals, "department"),
		[availableProfessionals],
	);
	const practiceTypeOptions = useMemo(
		() => uniqueProfessionalValues(availableProfessionals, "practiceType"),
		[availableProfessionals],
	);
	const filteredProfessionals = useMemo(() => {
		return availableProfessionals.filter((professional) => {
			if (showSelectedOnly && !selectedIds.has(professional.profId)) {
				return false;
			}
			return (
				professionalMatchesFilters(professional, professionalFilters) &&
				professionalMatchesSearch(professional, professionalSearch)
			);
		});
	}, [
		availableProfessionals,
		professionalFilters,
		professionalSearch,
		selectedIds,
		showSelectedOnly,
	]);
	const activeFilterCount =
		(professionalFilters.status === "all" ? 0 : 1) +
		(professionalFilters.role === "all" ? 0 : 1) +
		(professionalFilters.profType === "all" ? 0 : 1) +
		(professionalFilters.department === "all" ? 0 : 1) +
		(professionalFilters.practiceType === "all" ? 0 : 1) +
		(professionalFilters.licensed === "all" ? 0 : 1);
	const isProfessionalListFiltered =
		Boolean(professionalSearch.trim()) ||
		showSelectedOnly ||
		activeFilterCount > 0;
	const filteredProfessionalIds = useMemo(
		() => filteredProfessionals.map((professional) => professional.profId),
		[filteredProfessionals],
	);
	const selectedFilteredCount = useMemo(
		() =>
			filteredProfessionalIds.filter((professionalId) =>
				selectedIds.has(professionalId),
			).length,
		[filteredProfessionalIds, selectedIds],
	);
	const selectedMode = modeDescription(draft.mode);

	const updateProfessionalFilter = <K extends keyof ProfessionalFilters>(
		field: K,
		value: ProfessionalFilters[K],
	) =>
		setProfessionalFilters((current) => ({
			...current,
			[field]: value,
		}));

	const loadProfessionals = useCallback(async () => {
		setProfessionalsLoading(true);
		setProfessionalsError(null);
		try {
			setProfessionals(await listAccessPolicyProfessionals(makeApiRequest));
		} catch (error) {
			setProfessionalsError(
				errorMessage(error, "Failed to load professionals."),
			);
		} finally {
			setProfessionalsLoading(false);
		}
	}, [makeApiRequest]);

	useEffect(() => {
		void loadProfessionals();
	}, [loadProfessionals]);

	const loadPolicy = useCallback(
		async (isCancelled: () => boolean = () => false) => {
			if (!clientCode) {
				setPolicy(null);
				setDraft(EMPTY_DRAFT);
				setPolicyError(null);
				setPolicyLoading(false);
				return;
			}

			setPolicyLoading(true);
			setPolicyError(null);
			setPolicy(null);
			setDraft(EMPTY_DRAFT);

			try {
				const nextPolicy = await getAccessPolicy(makeApiRequest, {
					clientCode,
					matterId: matterId || null,
				});
				if (isCancelled()) return;
				setPolicy(nextPolicy);
				setDraft(draftFromPolicy(nextPolicy));
			} catch (error) {
				if (isCancelled()) return;
				setPolicyError(errorMessage(error, "Failed to load access policy."));
			} finally {
				if (!isCancelled()) setPolicyLoading(false);
			}
		},
		[clientCode, makeApiRequest, matterId],
	);

	useEffect(() => {
		let cancelled = false;
		void loadPolicy(() => cancelled);

		return () => {
			cancelled = true;
		};
	}, [loadPolicy]);

	const confirmDiscardDraft = () =>
		!dirty ||
		window.confirm("Discard the unsaved changes to this access policy?");

	const selectClient = (nextClientCode: string) => {
		if (!confirmDiscardDraft()) return;
		setClientCode(nextClientCode);
		setMatterId("");
		setProfessionalSearch("");
		setShowSelectedOnly(false);
	};

	const selectMatter = (nextMatterId: string) => {
		if (!confirmDiscardDraft()) return;
		setMatterId(nextMatterId);
		setProfessionalSearch("");
		setShowSelectedOnly(false);
	};

	const savePolicy = async () => {
		if (!clientCode || !policy || !dirty) return;
		setSaving(true);
		try {
			const savedPolicy = await replaceAccessPolicy(
				makeApiRequest,
				buildReplaceAccessPolicyBody(clientCode, matterId || null, draft),
			);
			setPolicy(savedPolicy);
			setDraft(draftFromPolicy(savedPolicy));
			toast.success("Access policy saved");
		} catch (error) {
			toast.error(errorMessage(error, "Failed to save access policy."));
		} finally {
			setSaving(false);
		}
	};

	const resetPolicy = async () => {
		if (!clientCode) return;
		const scopeName = matterId
			? `${clientCode} / ${matterId}`
			: `client ${clientCode}`;
		if (
			!window.confirm(
				`Clear the ${scopeName} policy and return it to an empty deny list?`,
			)
		) {
			return;
		}

		setSaving(true);
		try {
			const resetPolicyResult = await resetAccessPolicy(makeApiRequest, {
				clientCode,
				matterId: matterId || null,
			});
			setPolicy(resetPolicyResult);
			setDraft(draftFromPolicy(resetPolicyResult));
			toast.success("Access policy cleared");
		} catch (error) {
			toast.error(errorMessage(error, "Failed to clear access policy."));
		} finally {
			setSaving(false);
		}
	};

	return (
		<main className={styles.adminViewWrapper}>
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
						<ShieldCheck className="size-6" />
						Matter Access
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						Control which professionals can see a client or matter.
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						disabled={!clientCode || !policy || policyLoading || saving}
						onClick={() => void resetPolicy()}
					>
						<RotateCcw />
						Clear this scope
					</Button>
					<Button
						className={styles.adminPrimaryButton}
						disabled={
							!clientCode || !policy || !dirty || policyLoading || saving
						}
						loading={saving}
						onClick={() => void savePolicy()}
					>
						<Save />
						Save changes
					</Button>
				</div>
			</div>

			<section className={styles.settingsSectionFullWidth}>
				<div className="mb-4">
					<h2 className="text-base font-semibold text-foreground">
						Policy scope
					</h2>
					<p className="mt-1 text-sm text-muted-foreground">
						Choose a client, then optionally narrow the policy to one matter.
					</p>
				</div>

				<div className="grid gap-4 md:grid-cols-2">
					<div className="space-y-1.5">
						<Label htmlFor="access-policy-client">Client</Label>
						<ComboBox
							id="access-policy-client"
							items={clientOptions}
							value={clientCode || null}
							onChange={selectClient}
							fields={clientSearchFields}
							getKey={(client) => client.code}
							getDisplayLabel={clientOptionLabel}
							placeholder="Select a client"
							disabled={mattersLoading}
							disabledMessage="Loading clients…"
							clearable
							onClear={() => selectClient("")}
						/>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="access-policy-matter">
							Matter{" "}
							<span className="font-normal text-muted-foreground">
								(optional)
							</span>
						</Label>
						<ComboBox
							id="access-policy-matter"
							items={matterOptions}
							value={matterId || null}
							onChange={selectMatter}
							fields={matterSearchFields}
							getKey={(matter) => matterScopeParts(matter).matterId}
							getDisplayLabel={matterOptionLabel}
							placeholder="All matters for this client"
							disabled={!clientCode || mattersLoading}
							disabledMessage={
								mattersLoading ? "Loading matters…" : "Select a client first"
							}
							clearable
							onClear={() => selectMatter("")}
						/>
					</div>
				</div>

				{clientCode && (
					<Alert className="mt-4">
						<AlertTitle>
							{matterId
								? `Matter policy: ${clientCode} / ${matterId}`
								: `Client policy: ${clientCode}`}
						</AlertTitle>
						<AlertDescription>
							{matterId
								? "This adds another access rule for this matter. It cannot grant access to someone denied by the client policy."
								: "This policy applies to every matter for the client. Matter-specific policies are evaluated in addition to it."}
						</AlertDescription>
					</Alert>
				)}
			</section>

			{!clientCode ? (
				<section className={styles.settingsSectionFullWidth}>
					<p className={styles.emptyText}>
						Select a client to view and manage its access policy.
					</p>
				</section>
			) : policyLoading ? (
				<section className={styles.settingsSectionFullWidth}>
					<p className={styles.loadingText}>Loading access policy…</p>
				</section>
			) : policyError ? (
				<section className={styles.settingsSectionFullWidth}>
					<div className="space-y-3">
						<p className="text-sm text-destructive">{policyError}</p>
						<Button
							variant="outline"
							size="sm"
							onClick={() => void loadPolicy()}
						>
							Try again
						</Button>
					</div>
				</section>
			) : (
				<>
					<section className={styles.settingsSectionFullWidth}>
						<div className="mb-4">
							<h2 className="text-base font-semibold text-foreground">
								Access mode
							</h2>
							<p className="mt-1 text-sm text-muted-foreground">
								Choose what membership in the list means.
							</p>
						</div>
						<div className="grid gap-3 md:grid-cols-2">
							{(["deny_list", "allow_list"] as const).map((mode) => {
								const details = modeDescription(mode);
								const selected = draft.mode === mode;
								return (
									<button
										key={mode}
										type="button"
										aria-pressed={selected}
										disabled={saving}
										onClick={() =>
											setDraft((current) => updateDraftMode(current, mode))
										}
										className={cn(
											"rounded-lg border p-4 text-left transition-colors",
											selected
												? "border-primary bg-primary/5 ring-1 ring-primary"
												: "border-border hover:bg-accent/40",
										)}
									>
										<span className="flex items-center justify-between gap-3">
											<span className="font-medium">{details.title}</span>
											<span
												className={cn(
													"size-4 rounded-full border",
													selected
														? "border-4 border-primary"
														: "border-muted-foreground",
												)}
											/>
										</span>
										<span className="mt-2 block text-sm text-muted-foreground">
											{details.description}
										</span>
									</button>
								);
							})}
						</div>

						{draft.mode === "allow_list" &&
							draft.professionalIds.length === 0 && (
								<Alert variant="destructive" className="mt-4">
									<AlertTriangle />
									<AlertTitle>Nobody will have access</AlertTitle>
									<AlertDescription>
										An empty allow list denies every professional at this scope.
									</AlertDescription>
								</Alert>
							)}
					</section>

					<section className={styles.settingsSectionFullWidth}>
						<div className="mb-4 flex flex-wrap items-start justify-between gap-3">
							<div>
								<h2 className="text-base font-semibold text-foreground">
									{selectedMode.listLabel}
								</h2>
								<p className="mt-1 text-sm text-muted-foreground">
									{draft.professionalIds.length} selected ·{" "}
									{filteredProfessionals.length} shown
									{dirty ? " · Unsaved changes" : ""}
								</p>
							</div>
							<div className="flex flex-wrap gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={
										saving ||
										filteredProfessionalIds.length === 0 ||
										selectedFilteredCount === filteredProfessionalIds.length
									}
									onClick={() =>
										setDraft((current) =>
											selectProfessionals(current, filteredProfessionalIds),
										)
									}
								>
									{isProfessionalListFiltered
										? "Select all results"
										: "Select all"}
								</Button>
								<Button
									variant="outline"
									size="sm"
									disabled={saving || selectedFilteredCount === 0}
									onClick={() =>
										setDraft((current) =>
											deselectProfessionals(current, filteredProfessionalIds),
										)
									}
								>
									{isProfessionalListFiltered
										? "Deselect all results"
										: "Deselect all"}
								</Button>
								<Button
									variant={showSelectedOnly ? "secondary" : "outline"}
									size="sm"
									onClick={() => setShowSelectedOnly((current) => !current)}
								>
									{showSelectedOnly ? "Show everyone" : "Show selected only"}
								</Button>
							</div>
						</div>

						<div className="mb-4 flex w-full max-w-2xl flex-wrap gap-2">
							<div className="relative min-w-64 flex-1">
								<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									value={professionalSearch}
									onChange={(event) =>
										setProfessionalSearch(event.target.value)
									}
									placeholder="Search name, email, role, department, practice, or ID"
									className="pl-9"
								/>
							</div>
							<Popover>
								<PopoverTrigger asChild>
									<Button variant="outline">
										<SlidersHorizontal />
										Filters
										{activeFilterCount > 0 && (
											<Badge variant="secondary">{activeFilterCount}</Badge>
										)}
									</Button>
								</PopoverTrigger>
								<PopoverContent align="end" className="w-80 space-y-4">
									<div className="flex items-center justify-between gap-3">
										<div>
											<p className="font-medium">Filter professionals</p>
											<p className="text-xs text-muted-foreground">
												Active professionals are shown by default.
											</p>
										</div>
										<Button
											variant="ghost"
											size="sm"
											onClick={() =>
												setProfessionalFilters(DEFAULT_PROFESSIONAL_FILTERS)
											}
										>
											Reset
										</Button>
									</div>

									<div className="grid gap-3 sm:grid-cols-2">
										<div className="space-y-1.5">
											<Label>Status</Label>
											<Select
												value={professionalFilters.status}
												onValueChange={(value) =>
													updateProfessionalFilter(
														"status",
														value as ProfessionalFilters["status"],
													)
												}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="active">Active only</SelectItem>
													<SelectItem value="inactive">
														Inactive only
													</SelectItem>
													<SelectItem value="all">All statuses</SelectItem>
												</SelectContent>
											</Select>
										</div>

										<div className="space-y-1.5">
											<Label>Licensed</Label>
											<Select
												value={professionalFilters.licensed}
												onValueChange={(value) =>
													updateProfessionalFilter(
														"licensed",
														value as ProfessionalFilters["licensed"],
													)
												}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="all">Any</SelectItem>
													<SelectItem value="licensed">Licensed</SelectItem>
													<SelectItem value="unlicensed">Unlicensed</SelectItem>
												</SelectContent>
											</Select>
										</div>

										{[
											{
												field: "profType" as const,
												label: "Professional type",
												options: profTypeOptions,
											},
											{
												field: "department" as const,
												label: "Department",
												options: departmentOptions,
											},
											{
												field: "practiceType" as const,
												label: "Practice type",
												options: practiceTypeOptions,
											},
											{
												field: "role" as const,
												label: "Application role",
												options: roleOptions,
											},
										].map(({ field, label, options }) => (
											<div key={field} className="space-y-1.5">
												<Label>{label}</Label>
												<Select
													value={professionalFilters[field]}
													onValueChange={(value) =>
														updateProfessionalFilter(field, value)
													}
												>
													<SelectTrigger>
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="all">All</SelectItem>
														{options.map((option) => (
															<SelectItem key={option} value={option}>
																{option}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
										))}
									</div>
								</PopoverContent>
							</Popover>
						</div>

						{professionalsLoading ? (
							<p className={styles.loadingText}>Loading professionals…</p>
						) : professionalsError ? (
							<div className="space-y-3">
								<p className="text-sm text-destructive">{professionalsError}</p>
								<Button
									variant="outline"
									size="sm"
									onClick={() => void loadProfessionals()}
								>
									Try again
								</Button>
							</div>
						) : filteredProfessionals.length === 0 ? (
							<p className={styles.emptyText}>
								{showSelectedOnly
									? "No selected professionals match these filters."
									: "No professionals match these filters."}
							</p>
						) : (
							<div className="max-h-[28rem] overflow-y-auto rounded-md border border-border">
								{filteredProfessionals.map((professional) => {
									const selected = selectedIds.has(professional.profId);
									return (
										<div
											key={professional.profId}
											className={cn(
												"flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0",
												"hover:bg-accent/40",
												selected && "bg-primary/5",
											)}
										>
											<Checkbox
												checked={selected}
												disabled={saving}
												onCheckedChange={() =>
													setDraft((current) =>
														toggleProfessional(current, professional.profId),
													)
												}
												aria-label={`${selected ? "Remove" : "Add"} ${professional.name || professional.email}`}
											/>
											<span className="min-w-0 flex-1">
												<span className="flex flex-wrap items-center gap-2">
													<span className="font-medium text-foreground">
														{professional.name || professional.email}
													</span>
													{!professional.isActive && (
														<Badge variant="outline">Inactive</Badge>
													)}
													{professional.profType && (
														<Badge variant="secondary">
															{professional.profType}
														</Badge>
													)}
													{professional.isLicensed && (
														<Badge variant="outline">Licensed</Badge>
													)}
												</span>
												<span className="block truncate text-sm text-muted-foreground">
													{professional.email ? `${professional.email} · ` : ""}
													{professional.profId}
												</span>
												<span className="mt-0.5 block truncate text-xs text-muted-foreground">
													{[
														professional.department &&
															`Department: ${professional.department}`,
														professional.practiceType &&
															`Practice: ${professional.practiceType}`,
														`Role: ${professional.role}`,
													]
														.filter(Boolean)
														.join(" · ")}
												</span>
											</span>
										</div>
									);
								})}
							</div>
						)}
					</section>
				</>
			)}
		</main>
	);
}
